/**
 * LA MÉTRIQUE du moteur de formules — ce que `disposerMath` sait de la police
 * dans laquelle ses glyphes seront dessinés.
 *
 * ═══ Pourquoi ce fichier existe ═══
 *
 * Le moteur a été écrit pour IBM Plex Mono : une chasse FIXE rend la largeur
 * d'un atome calculable sans table. Une police mathématique, elle, est
 * PROPORTIONNELLE — et poser ses glyphes dans des cases de 0,6 cadratin les
 * décolle les uns des autres (un exposant flotte loin de sa base : c'est le
 * premier défaut de l'essai du 8 octobre 2026).
 *
 * La métrique dit donc trois choses, et rien d'autre :
 *
 *  1. l'AVANCE de chaque caractère, lue dans la police (`metrique-police.ts`,
 *     fichier engendré) ;
 *  2. les HAUTEURS de la police — capitale, œil, descendante — et la hauteur
 *     où se pose un exposant ;
 *  3. quelles lettres passent en ITALIQUE (les variables), lesquelles restent
 *     dans la police du texte (`\text{…}`, une unité).
 *
 * ═══ Pourquoi un choix, et pas un remplacement ═══
 *
 * `METRIQUE_MONO` reste le DÉFAUT : la console du tuteur et l'image de
 * construction dessinent encore en IBM Plex Mono, et une liste d'affichage
 * calculée pour une autre police les décalerait. Chaque rendu choisit sa
 * métrique UNE FOIS, au chargement (`choisirMetriqueMath`), du même geste
 * qu'il charge sa police. Le jour où tous les rendus portent la même police,
 * ce choix se retire et la métrique devient une constante.
 */
export const METRIQUE_MONO = {
    nom: 'ibm-plex-mono',
    polices: null,
    avanceFixe: 0.6,
    echelle: 1,
    montee: 0.74,
    oeil: 0.52,
    descente: 0.24,
    elevationExposant: 0.42,
    airScript: 0.04,
    correctionItalique: 0,
    airFraction: 0,
};
const enTable = (t) => {
    const sortie = new Map();
    [...t.caracteres].forEach((c, i) => sortie.set(c, (t.avances[i] ?? 600) / 1000));
    return sortie;
};
/** Construit une métrique proportionnelle à partir des tables engendrées. */
export function metriqueProportionnelle(reglages, tables) {
    return {
        ...reglages,
        avanceFixe: 0.6,
        polices: {
            droit: enTable(tables.droit),
            italique: enTable(tables.italique),
            texte: enTable(tables.texte),
            symbole: enTable(tables.symbole ?? { caracteres: '', avances: [] }),
        },
    };
}
let courante = METRIQUE_MONO;
/** Le rendu dit dans quelle police il dessine. Une fois, au chargement. */
export function choisirMetriqueMath(metrique) {
    courante = metrique;
}
export const metriqueMath = () => courante;
const LETTRE = /\p{L}/u;
/**
 * Un atome est du TEXTE — et non une variable — dès qu'il porte deux lettres
 * de suite (`cm`, `facteurs`) ou une lettre et une espace (`3 h`, la grandeur
 * que `\text` colle à son nombre). Une variable est une lettre seule : `x`,
 * `−n`, `2x`.
 */
const EST_DU_TEXTE = /\p{L}[\p{L}\p{M}’'-]*\p{L}|\p{L}.*[\s\u00A0\u202F]|[\s\u00A0\u202F].*\p{L}/u;
/**
 * Découpe le texte d'un glyphe en tranches d'une seule police, chacune avec sa
 * largeur. Chiffres et signes : police droite. Lettre seule : italique. Mot :
 * police du texte courant.
 */
export function trancherGlyphe(texte, role, m = courante) {
    const p = m.polices;
    if (p === null)
        return [{ texte, largeur: [...texte].length * m.avanceFixe }];
    const somme = (t, table) => [...t].reduce((s, c) => s + (table.get(c) ?? m.avanceFixe), 0);
    if (role === 'operande' && EST_DU_TEXTE.test(texte)) {
        return [{ texte, police: 'texte', largeur: somme(texte, p.texte) }];
    }
    const tranches = [];
    for (const c of texte) {
        let police;
        let largeur;
        if (role === 'operande' && LETTRE.test(c) && p.italique.has(c)) {
            police = 'italique';
            largeur = p.italique.get(c) ?? m.avanceFixe;
        }
        else if (p.droit.has(c)) {
            largeur = p.droit.get(c) ?? m.avanceFixe;
        }
        else if (p.symbole.has(c)) {
            police = 'symbole';
            largeur = p.symbole.get(c) ?? m.avanceFixe;
        }
        else {
            largeur = m.avanceFixe;
        }
        const derniere = tranches[tranches.length - 1];
        if (derniere !== undefined && derniere.police === police) {
            derniere.texte += c;
            derniere.largeur += largeur;
        }
        else {
            tranches.push(police === undefined ? { texte: c, largeur } : { texte: c, police, largeur });
        }
    }
    return tranches;
}
//# sourceMappingURL=metrique.js.map